import React, {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from "react";
import styles from "../../components/live-support/liveSupportComponent.module.scss";
import sendIcon from "../../assets/images/pages/home/live-support/icon/sendIcon.svg";
import plusIcon from "../../assets/images/pages/home/live-support/icon/plus.svg";
import docSend from "../../assets/images/pages/home/live-support/icon/doc.png";
import imgSendIcon from "../../assets/images/pages/home/live-support/icon/img.png";
import Pusher from "pusher-js";
import { getCookie } from "../../api/utils/utils";
import SupportServices from "./services/SupportService";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface Message {
  id: number;
  message: string;
  isUser: boolean;
  is_file: number;
  timestamp: string;
  isParent?: boolean;
  templateId?: number;
  hasChildren?: boolean;
  isContent?: boolean;
  isLink?: boolean;
}

interface Props {
  setMessages: Dispatch<SetStateAction<Message[]>>;
}

const LiveChatSection = ({ setMessages }: Props) => {
  const [isDropDown, setIsDropDown] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const id = getCookie("bsc-guest-chat-id");
  const translations = useSelector(
    (state: RootState) => state.translations?.data
  );

  const dropDownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target as Node)
      ) {
        setIsDropDown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!id) return;
    Pusher.logToConsole = true;
    const pusher = new Pusher("66fa74a20735682f38a2", {
      cluster: "ap2",
      forceTLS: true,
      authEndpoint: "/pusher/auth",
    });

    const channel = pusher.subscribe("chat-channel");

    pusher.connection.bind("state_change", (states: any) => {
      console.log("Pusher connection state:", states);
    });

    channel.bind("pusher:subscription_succeeded", () => {
      console.log("Successfully subscribed to channel!");
    });
    const eventName = `user-${id}`;
    console.log("Binding to event:", eventName);

    const handler = (data: any) => {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const newMessage = {
        content: parsedData.content,
        is_file: parsedData.is_file,
        sender_type: parsedData.sender_type,
        hour: parsedData.hour,
        date: parsedData.date,
      };
      addMessageInstantly(newMessage);
    };

    channel.bind(eventName, handler);

    return () => {
      channel.unbind(eventName, handler);
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [id]);

  const addMessageInstantly = (message: any) => {
    const userMessage: Message = {
      id: Number(id),
      message: message.content,
      is_file: message.is_file,
      isUser: message.sender_type === "user" ? true : false,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
  };

  const sendMessage = (isFile: number, messageContent: string | File) => {
    if (
      !messageContent ||
      (!isFile && (messageContent as string).trim() === "")
    )
      return;

    let payload: any;
    if (isFile) {
      payload = new FormData();
      payload.append("is_file", "1");
      payload.append("content", messageContent as File);
    } else {
      payload = { is_file: 0, content: messageContent };
    }

    SupportServices.sendMessage(Number(id), payload)
      .catch((err) => console.error(err))
      .finally(() => {
        if (!isFile) setInputMessage("");
      });
  };

  return (
    <div className={styles.inputArea}>
      {isDropDown && (
        <div ref={dropDownRef} className={styles.dropDown}>
          <div className={styles.dropDownContent}>
            <label
              htmlFor="image-upload"
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <img src={imgSendIcon} alt="Şəkil göndərin" />
              <p>{translations?.send_image}</p>
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                setIsDropDown(false);
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    sendMessage(1, file);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>

          <div className={styles.dropDownContent}>
            <label
              htmlFor="file-upload"
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <img src={docSend} alt="Fayl göndərin" />
              <p>{translations?.send_file}</p>
            </label>
            <input
              id="file-upload"
              type="file"
              style={{ display: "none" }}
              accept=".pdf,.doc,.docx,.txt,.zip,.rar,.json"
              onChange={(e) => {
                setIsDropDown(false);
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    sendMessage(1, file);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>
        </div>
      )}
      <form className={styles.form}>
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder={translations?.write_message}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              const value = (e.target as HTMLTextAreaElement).value;
              sendMessage(0, value);
            }
          }}
          className={styles.liveChatInput}
        />
        <div>
          <img
            onClick={() => setIsDropDown(!isDropDown)}
            className={styles.sendButton}
            src={plusIcon}
            alt="Attach"
          />
        </div>
        <button
          className={styles.sendButton}
          type="submit"
          onClick={(e) => {
            sendMessage(0, inputMessage);
            e.preventDefault();
          }}
        >
          <img src={sendIcon} alt="Send" />
        </button>
      </form>
    </div>
  );
};

export default LiveChatSection;
